import { ImprList, Modal } from '@imprivata-cloud/components';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColumnType } from 'antd/es/table';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ConsentGrantType, ConsentType } from '../../api/constants';
import { getSelfServiceLandingPageData, submitConsent } from '../../api/selfServiceServices';
import type { SelfServiceLandingPageDataResponse } from '../../api/types';
import QuestionImg from '../../assets/icons/question.svg?react';
import EmptyImage from '../../assets/self-service-authenticator-list-empty-table.svg?react';
import getRelativeTime from '../../common/relativeTime';
import AuthenticatorActionItems from '../../components/authenticatorActionItems/AuthenticatorActionItems';
import AuthenticatorNameAndIcon from '../../components/authenticatorNameAndIcon/AuthenticatorNameAndIcon';
import { AppError } from '../../errorHandler/errors';
import { useSession } from '../../common/authorization/context/Session';
import {
  landingPageDataTanstackCacheKey,
  useUnenrollAuthenticator,
} from '../../portal/containers/users/hooks/useAuthenticators';
import { useErrorContext } from '../../errorHandler/context/ErrorContext';
import './AuthenticatorList.less';
import { useSelfServiceData } from '../utils/useSelfServiceData';

const columns: ColumnType[] = [
  {
    title: 'Name',
    dataIndex: 'name-320',
    key: 'name-320',
    responsive: ['xs'],
    render: (_, record) => (
      <>
        <AuthenticatorNameAndIcon />
        <span style={{ color: 'var(--grey-50)', paddingTop: '9px' }}>{record.lastUsed}</span>
      </>
    ),
  },
  {
    title: 'Name',
    dataIndex: 'name',
    key: 'name',
    responsive: ['sm'],
    render: () => <AuthenticatorNameAndIcon />,
  },
  {
    title: 'Last Used',
    dataIndex: 'lastUsed',
    key: 'lastUsed',
    responsive: ['sm'],
    render: (_, record) => <span style={{ color: 'var(--grey-50)' }}>{record.lastUsed}</span>,
  },
  {
    dataIndex: 'action',
    key: 'action',
    render: (_, record) => (
      <AuthenticatorActionItems
        key={record.key}
        factorType={record.factorType}
        onRemove={record.onRemove}
        onRevokeConsent={record?.onRevokeConsent}
      />
    ),
  },
];

const AuthenticatorList = () => {
  const { t } = useTranslation();
  const { emitError } = useErrorContext();
  const queryClient = useQueryClient();

  const { tenantId } = useSession();
  const { mutate, isPending: isLoadingUnenrollAuthenticator } = useUnenrollAuthenticator(tenantId ?? '', {
    onSettled: () => setAuthenticatorToRemove(undefined),
    onError: () => emitError(AppError.fromMessage('Error removing authenticator')),
  });

  const [authenticatorToRemove, setAuthenticatorToRemove] = useState<string>();
  const [consentRevokeDialog, setConsentRevokeDialog] = useState(false);
  const [dialogLoading, setDialogLoading] = useState(false);
  const {
    data: selfServiceLandingPageData,
    isError: isErrorSelfServiceLandingPageData,
    isLoading: isLoadingSelfServiceLandingPageData,
    refetch: refetchSelfServiceLandingPageData,
  } = useSelfServiceData();

  if (isErrorSelfServiceLandingPageData) {
    emitError(AppError.fromMessage('Error fetching authenticators'));
  }

  const consentInfo = selfServiceLandingPageData?.consentInfos.find(
    (info) => info.consentType === ConsentType.BIOMETRICS,
  );

  const authData = selfServiceLandingPageData?.authenticators
    ?.filter((a) => a.factorType === 'face') // Only for Q2 release
    .map((auth) => {
      return {
        key: auth.authenticatorId,
        factorType: auth.factorType,
        lastUsed: getRelativeTime(auth.lastUsedAt),
        onRemove: () => setAuthenticatorToRemove(auth.authenticatorId),
        onRevokeConsent:
          consentInfo?.grantType === ConsentGrantType.GRANTED ? () => setConsentRevokeDialog(true) : undefined,
      };
    });

  const onRemove = () => {
    if (authenticatorToRemove === undefined) {
      return; // To satisfy ts
    }
    mutate(authenticatorToRemove, {
      onSuccess: () => {
        // remove the authenticator from the React-Query cache list
        queryClient.setQueryData<SelfServiceLandingPageDataResponse>([landingPageDataTanstackCacheKey], (oldData) => {
          return {
            ...oldData,
            authenticators: oldData?.authenticators?.filter((auth) => auth.authenticatorId !== authenticatorToRemove),
          } as SelfServiceLandingPageDataResponse;
        });
      },
    });
  };

  const onRevokeConsent = () => {
    if (!consentRevokeDialog) {
      return; // To satisfy ts
    }
    setDialogLoading(true);
    submitConsent({
      consentType: ConsentType.BIOMETRICS,
      residenceState: consentInfo?.residenceState || '',
      grantType: ConsentGrantType.NOT_GRANTED,
      documentName: consentInfo?.documentName || '',
    })
      .then(() => {
        // revoking consent unenrolls the authenticator
        queryClient.invalidateQueries({
          queryKey: [landingPageDataTanstackCacheKey],
          refetchType: 'all',
        });
        refetchSelfServiceLandingPageData(); // invalidate query not working
      })
      .catch(() => {
        emitError(AppError.fromMessage('Error revoking consent'));
      })
      .finally(() => {
        setDialogLoading(false);
        setConsentRevokeDialog(false);
      });
  };

  return (
    <>
      <Modal
        open={!!authenticatorToRemove}
        onCancel={() => setAuthenticatorToRemove(undefined)}
        cancelButtonProps={{ type: 'primary' }}
        okButtonProps={{ type: 'default', loading: isLoadingUnenrollAuthenticator }}
        okText={t('self-service.authenticator-list.confirm-remove-face-btn')}
        cancelText={t('self-service.authenticator-list.cancel-remove-face')}
        onOk={onRemove}
        onClose={() => setAuthenticatorToRemove(undefined)}
        centered={true}
        closeIcon={false}
        className="self-service-confirmation-modal"
      >
        <QuestionImg className="modal-graphic" />
        <h2>{t('self-service.authenticator-list.remove-authenticator-title')}</h2>
        <p>{t('self-service.authenticator-list.confirm-remove-face')}</p>
      </Modal>

      <Modal
        open={!!consentRevokeDialog}
        onCancel={() => setConsentRevokeDialog(false)}
        cancelButtonProps={{ type: 'primary' }}
        okButtonProps={{ type: 'default', loading: dialogLoading }}
        okText={t('self-service.authenticator-list.confirm-revoke-consent')}
        cancelText={t('self-service.authenticator-list.cancel-revoke-consent')}
        onOk={onRevokeConsent}
        onClose={() => setConsentRevokeDialog(false)}
        centered={true}
        closeIcon={false}
        className="self-service-confirmation-modal"
      >
        <QuestionImg className="modal-graphic" />
        <h2>{t('self-service.authenticator-list.revoke-consent-title')}</h2>
        <p>{t('self-service.authenticator-list.revoke-consent-content')}</p>
      </Modal>

      <ImprList
        loading={isLoadingSelfServiceLandingPageData}
        columns={columns}
        dataSource={authData as Record<string, unknown>[]}
        showHeader={false}
        pagination={false}
        enableSearch={false}
        headerExtraComponents={[
          <div key="table-extra-header" className="authenticators-table-title">
            {t('self-service.authenticator-list.table-title')}
          </div>,
        ]}
        locale={{
          emptyText: (
            <div className={'empty-list-container'}>
              <EmptyImage style={{ margin: '1rem' }} />
              <div className={'empty-list-container-title'}>{t('self-service.authenticator-list.empty-text')}</div>
            </div>
          ),
        }}
      />
    </>
  );
};

export default AuthenticatorList;
